import React, { useCallback, useMemo } from 'react';
import { merge } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import RightModalLayout from '../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import FormProviderMC from '../components/FormProviderMC';
import httpService from '../../services/httpService';
import ManageFunnelStepOpened from './componentsFunnelStep/ManageFunnelStepOpened';
import ManageFunnelStepClosed from './componentsFunnelStep/ManageFunnelStepClosed';
import { getSchemaStep } from './validations/funnelStepSchema';
import SelectFunnelStepByType from './componentsFunnelStep/SelectFunnelStepByType';

const buildStep = (step) => ({
  funnel_step: {
    name: step.title,
    probability: step.probability,
    stagnation_time: step.stagnation_time,
    type_step: step.type_step,
    ...(step.type_step === 'step'
      ? {}
      : {
        reasons_attributes: step.reasons
          ?.filter(({ reason }) => reason && reason.trim() !== '')
          .map(({ id, reason, _created, _destroy }) => ({
            id: _created ? null : id,
            reason: reason.trim(),
            _destroy
          }))
      })
  },
  id: step?.id
});

const FunnelStep = ({
  selectedFunnel,
  stepSelected,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Funnels');
  const { currentRetailerUser = {} } = useSelector((reduxState) => reduxState.mainReducer);
  const canManageFunnelSteps = Boolean(currentRetailerUser?.can_manage_funnel_steps);

  const closeModal = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'FunnelStep' }
    });
  }, [dispatch]);

  const titleLabel = stepSelected?.id ? t('funnelStepSidebar.update') : t('funnelStepSidebar.create');

  const methods = useForm({
    resolver: yupResolver(getSchemaStep(t, selectedFunnel.funnel_steps, stepSelected?.internal_id)),
    defaultValues: merge({}, {
      title: '',
      probability: 0,
      stagnation_time: null,
      reasons: [],
    }, stepSelected),
  });

  const handleSubmit = (values) => {
    if (!canManageFunnelSteps) {
      showtoast(t('access_denied.title'));
      return;
    }

    let endPoint = `${ENV.DEAL_ENGINE_BASE_URL}/funnels/${selectedFunnel.web_id}/funnel_steps`;

    if (values?.id) {
      endPoint = `${endPoint}/${values.id}`;
    }

    httpService.save(endPoint, buildStep(values)).then((data) => {
      if (values?.id) {
        const newStep = { id: values.internal_id, name: data.name, web_id: values.id, reasons: data.reasons };
        dispatch({ type: 'UPDATE_FUNNELS', data: newStep, funnelId: selectedFunnel.web_id });
      }
      else {
        const { internal_id, title, id } = data.step;
        const newStep = { id: internal_id, name: title, web_id: id, reasons: data.step.reasons };
        dispatch({ type: 'ADD_FUNNELS', data: newStep, funnelId: selectedFunnel.web_id });
      }
      dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
    }).catch((error) => {
      showtoast(error?.message || error);
    });
  };

  const typeStep = methods.watch('type_step');

  const Content = useMemo(() => {
    switch (typeStep) {
      case 'step':
        return ManageFunnelStepOpened;
      case 'won':
        return ManageFunnelStepClosed;
      case 'lost':
        return ManageFunnelStepClosed;
      default:
        return SelectFunnelStepByType;
    }
  }, [typeStep]);

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={titleLabel}
        submitAction={typeStep
          ? methods.handleSubmit(handleSubmit)
          : undefined
        }
        cancelAction={typeStep
          ? () => stepSelected?.type_step
            ? closeModal()
            : methods.setValue('type_step', null, { shouldDirty: true, shouldValidate: true })
          : undefined
        }
        submitLabel={stepSelected?.id ? t('funnelStepSidebar.button.update') : t('funnelStepSidebar.button.create')}
        cancelLabel={t('funnelStepSidebar.button.back')}
        buttonSubmitType="submit"
        closeAction={closeModal}
      >
        <FormProviderMC methods={methods} onSubmit={methods.handleSubmit(handleSubmit)}>
          <Content />
        </FormProviderMC>
      </RightModalLayout>
    </div>
  );
};

export default FunnelStep;
